/**
 * Discount Preview — API Module
 *
 * Kapselt alle Netzwerk- und Storage-Zugriffe der Discount-Engine:
 *   - Shopify Storefront GraphQL (ephemerer cartCreate für Preview-Preise)
 *   - Cart AJAX API (cart.js, cart/update.js)
 *   - Section-Replace nach cart/update.js
 *   - localStorage-Cache für Preview-Preise
 *
 * Keine Abhängigkeit zum State. Reine Funktionen, die Inputs in Outputs
 * übersetzen (oder Side-Effects via fetch/DOM).
 *
 * Exponiert: window.__dpApi
 *   - isEnabled, config
 *   - readCache(), writeCache(entries), invalidateCache(), cacheKey(codes, ids)
 *   - gidToNumeric(gid), toCents(amount)
 *   - fetchCart(), pushCodeToCart(code), fireCartChange(sections)
 *   - fetchStorefrontPreview(variantIds, codes)
 */
(function () {
  'use strict';

  const STORAGE_KEY_CACHE = 'zh_discount_preview_cache_v5';
  // 1 Stunde Cache-TTL. Auf localStorage gespeichert (cross-session), damit
  // Wiederkehr-User mit aktivem Code sofort warmen Cache sehen, kein Page-Load-
  // Flicker mehr nach Tab-Close/Reopen.
  const CACHE_TTL_MS = 60 * 60 * 1000;
  // Cap auf Entry-Anzahl, damit der Cache nicht unbegrenzt waechst und
  // QuotaExceededError-Kaskaden bei Drittanbietern (Elevar/GTM) ausloest.
  // FIFO-Eviction ueber JS-Insertion-Order der Keys: runPendingFetch loescht
  // einen Key vor dem Re-Set, damit aktive Eintraege ans Ende rotieren.
  const MAX_CACHE_ENTRIES = 100;
  const QUOTA_FALLBACK_ENTRIES = 50;
  const STOREFRONT_PATH = '/api/{version}/graphql.json';

  const config = (window.themeVariables && window.themeVariables.discountPreview) || {};
  const shopInfo = (window.themeVariables && window.themeVariables.shop) || {};

  const isEnabled = Boolean(config.enabled && config.storefrontToken && shopInfo.domain);

  // Single-Variant-Cart-Mutation: pro Variant ein eigener ephemerer Cart.
  // Wir bauen daraus eine aliased Batch-Mutation (siehe buildBatchMutation),
  // damit alle Variants in einem HTTP-Request gehen, aber jede Variant ihren
  // eigenen isolierten Cart bekommt. Das macht die Preisberechnung deterministisch
  // und unabhängig von der Cart-Komposition auf der jeweiligen Page.
  const PREVIEW_CART_FRAGMENT = `
    cart {
      cost {
        subtotalAmount { amount currencyCode }
        totalAmount { amount currencyCode }
      }
      discountCodes { code applicable }
      lines(first: 1) {
        nodes {
          quantity
          merchandise { ... on ProductVariant { id } }
          cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
          }
          discountAllocations {
            discountedAmount { amount currencyCode }
            ... on CartCodeDiscountAllocation { code }
          }
        }
      }
    }
    userErrors { field code message }
  `;

  /**
   * Baut eine aliased Multi-cartCreate-Mutation: pro Variant eine eigene
   * cartCreate-Aliasing-Variable. Codes kommen via shared GraphQL-Variable
   * (sicher escaped). Variant-IDs werden direkt in den Query-String gebakt
   * (numerisch geparsed, daher injection-safe).
   */
  function buildBatchMutation(variantIds) {
    const aliases = variantIds.map((id, idx) => {
      const numericId = parseInt(id, 10);
      if (!Number.isFinite(numericId)) return '';
      return `
        v${idx}: cartCreate(input: {
          lines: [{ merchandiseId: "gid://shopify/ProductVariant/${numericId}", quantity: 1 }],
          discountCodes: $codes
        }) { ${PREVIEW_CART_FRAGMENT} }`;
    }).filter(Boolean).join('\n');
    return `mutation Preview($codes: [String!]!) { ${aliases} }`;
  }

  // Preview-Cache liegt auf localStorage statt sessionStorage. Browser-Tab
  // schließen verliert dann den Cache nicht mehr — Wiederkehr-User sehen
  // sofort die gerabatteten Preise ohne weiteren API-Roundtrip.
  function readCache() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CACHE);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem(STORAGE_KEY_CACHE);
        return {};
      }
      return parsed.entries || {};
    } catch (e) {
      return {};
    }
  }

  // Liefert entweder die Original-Referenz (Hot-Path, unter Cap) oder eine
  // neue, getrimmte Kopie (ueber Cap). Caller duerfen das Resultat NICHT als
  // immutable annehmen: bei Resultat === entries wuerde eine Mutation den
  // Caller-Cache veraendern. Aktuell nicht problematisch, weil der einzige
  // Caller writeCache ist und nach diesem Aufruf nicht weiter mutiert.
  function limitEntries(entries, max) {
    const keys = Object.keys(entries);
    if (keys.length <= max) return entries;
    // Keep the last `max` insertion-ordered keys: in runPendingFetch wird der
    // jeweils geschriebene Key per delete-then-set ans Ende rotiert, daher
    // entspricht insertion-order grob LRU.
    const keep = keys.slice(keys.length - max);
    const trimmed = {};
    for (let i = 0; i < keep.length; i++) trimmed[keep[i]] = entries[keep[i]];
    return trimmed;
  }

  function writeCache(entries) {
    const capped = limitEntries(entries, MAX_CACHE_ENTRIES);
    try {
      localStorage.setItem(
        STORAGE_KEY_CACHE,
        JSON.stringify({
          expiresAt: Date.now() + CACHE_TTL_MS,
          entries: capped
        })
      );
    } catch (e) {
      // Quota voll — meistens durch Drittanbieter (Elevar/GTM) im selben
      // localStorage. Kann theoretisch auch ein JSON.stringify-Fehler
      // (z.B. circular ref) oder SecurityError (Private Mode) sein — daher
      // einmal warnen, damit Quota- und Stringify-Failures im Diag-Log
      // unterscheidbar bleiben.
      // eslint-disable-next-line no-console
      console.warn('[discount-preview] writeCache failed, attempting trim+retry', e);
      // Aggressiv trimmen und ein Mal retry. Wenn das auch failt, Cache
      // komplett raeumen, damit der naechste Read sauber ist.
      try {
        const aggressive = limitEntries(capped, QUOTA_FALLBACK_ENTRIES);
        localStorage.setItem(
          STORAGE_KEY_CACHE,
          JSON.stringify({
            expiresAt: Date.now() + CACHE_TTL_MS,
            entries: aggressive
          })
        );
      } catch (e2) {
        try { localStorage.removeItem(STORAGE_KEY_CACHE); } catch (e3) {}
      }
    }
  }

  function invalidateCache() {
    try { localStorage.removeItem(STORAGE_KEY_CACHE); } catch (e) {}
    // Auch sessionStorage clearen (Legacy von vor dem Cache-Migrate)
    try { sessionStorage.removeItem(STORAGE_KEY_CACHE); } catch (e) {}
  }

  function cacheKey(codes, variantIds) {
    const c = [...codes].sort().join('|');
    const v = [...variantIds].sort((a, b) => Number(a) - Number(b)).join(',');
    return `${c}::${v}`;
  }

  function gidToNumeric(gid) {
    if (!gid) return null;
    const match = String(gid).match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  function toCents(amountString) {
    if (amountString == null) return 0;
    const n = Number(amountString);
    if (Number.isNaN(n)) return 0;
    return Math.round(n * 100);
  }

  /** Holt aktuellen Cart-State von Shopify. */
  async function fetchCart() {
    try {
      const res = await fetch(window.Shopify.routes.root + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  /** Sammelt Section-IDs (cart-drawer + main-cart) die nach Code-Apply neu gerendert werden sollen. */
  function collectCartSectionIds() {
    // Pattern aus theme.js: mutable array via detail.sections weiterreichen,
    // Listeners können IDs pushen. Set-Dedup passiert am Ende.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );
    const sections = new Set(sectionsToBundle);
    // Section-IDs über DOM finden: cart-drawer, alle Sections mit "cart" im ID,
    // sowie die Section, die ein <main-cart>-Element enthält (auf /cart, dort
    // ist die Section-ID template-spezifisch ohne "cart" im Namen).
    document.querySelectorAll('cart-drawer[id^="shopify-section-"], #shopify-section-cart, [id^="shopify-section-"][id*="cart"]').forEach((el) => {
      const m = el.id.match(/^shopify-section-(.+)$/);
      if (m) sections.add(m[1]);
    });
    // Auf /cart befindet sich der Cart-Markup in einer template--xxx__main
    // Section ohne "cart" im Namen. Wir erkennen sie an einer cart-Markup-Klasse.
    if (window.location.pathname.replace(/\/$/, '') === '/cart') {
      document.querySelectorAll('[id^="shopify-section-"]').forEach((sec) => {
        if (sec.querySelector('.cart, [data-cart-page], main-cart, cart-page')) {
          sections.add(sec.id.replace(/^shopify-section-/, ''));
        }
      });
    }
    return Array.from(sections);
  }

  /** Wendet einen Code via Cart-Update-API auf den echten Cart an. */
  async function pushCodeToCart(code) {
    try {
      const sectionIds = collectCartSectionIds();
      const body = { discount: code };
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }
      const res = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        return { ok: false, status: res.status };
      }
      const json = await res.json();
      return { ok: true, cart: json, sections: json.sections || null };
    } catch (e) {
      return { ok: false, error: e };
    }
  }

  /**
   * Ersetzt eine Section direkt im DOM mit dem HTML aus /cart/update.js sections.
   *
   * SECURITY: newHTML MUSS aus Shopifys Sections-API stammen (/cart/update.js
   * mit sections-Parameter). Die API rendert Liquid-Templates aus unserem
   * Theme-Code server-seitig und liefert HTML aus vertrauenswürdiger Quelle
   * (Shopify-Backend, gleiches Origin). DOMParser + importNode sanitized NICHT,
   * Scripts würden ausgeführt. Niemals nutzergesteuerten oder externen
   * Inhalt hier durchreichen.
   */
  function replaceSectionHTML(sectionId, newHTML) {
    if (!newHTML || typeof newHTML !== 'string') return false;
    const el = document.getElementById('shopify-section-' + sectionId);
    if (!el) return false;
    const parser = new DOMParser();
    const doc = parser.parseFromString(newHTML, 'text/html');
    const fresh = doc.getElementById('shopify-section-' + sectionId);
    const sourceNode = fresh || doc.body;
    while (el.firstChild) el.removeChild(el.firstChild);
    Array.from(sourceNode.childNodes).forEach((node) => {
      el.appendChild(document.importNode(node, true));
    });
    return true;
  }

  /** Triggert das Theme-eigene cart:change-Event, damit cart-drawer/main-cart re-rendern. */
  async function fireCartChange(sections) {
    try {
      const cart = await fetchCart();
      if (!cart) return;
      if (sections) cart.sections = sections;
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'discount:change', cart: cart }
        })
      );
      // Fallback: wenn der Cart-Page main-section nicht selbst auf cart:change
      // hört, ersetzen wir das HTML direkt. Sections, die kein "cart" im
      // Namen haben (template-spezifisch), werden vom Theme nicht angefasst.
      if (sections && typeof sections === 'object') {
        Object.keys(sections).forEach((id) => {
          if (!id.includes('cart')) replaceSectionHTML(id, sections[id]);
        });
      }
    } catch (e) {
      // ignore
    }
  }

  /** Storefront-API-Call: ephemerer Cart mit Variants + Codes, lest Preise. */
  async function fetchStorefrontPreview(variantIds, codes) {
    if (!isEnabled) return null;
    if (!variantIds.length) return null;

    const version = config.apiVersion || '2026-04';
    const endpoint = `https://${shopInfo.domain}${STOREFRONT_PATH.replace('{version}', version)}`;

    // Aliased Batch-Mutation: pro Variant ein eigener cartCreate. Damit ist
    // die Preisberechnung pro Variant deterministisch — keine Cross-Variant-
    // Interferenz durch Order-Discount-Allokationen. Nur ein HTTP-Request.
    const query = buildBatchMutation(variantIds);

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Shopify-Storefront-Access-Token': config.storefrontToken
        },
        body: JSON.stringify({
          query: query,
          variables: { codes: codes }
        })
      });

      if (!res.ok) {
        console.warn('[discount-preview] Storefront API HTTP error', res.status, await res.text().catch(() => ''));
        return null;
      }

      const json = await res.json();

      if (json.errors && json.errors.length) {
        console.warn('[discount-preview] GraphQL errors', json.errors);
        return null;
      }

      const previewMap = {};
      let appliedCodes = [];

      // Pro Alias (v0, v1, ...) den entsprechenden Single-Variant-Cart parsen.
      variantIds.forEach((rawVid, idx) => {
        const alias = 'v' + idx;
        const data = json.data && json.data[alias];
        if (!data || !data.cart) return;

        const userErrors = data.userErrors || [];
        if (userErrors.length) {
          console.warn('[discount-preview] cartCreate userErrors for ' + rawVid, userErrors);
        }

        const cart = data.cart;
        const aliasApplied = (cart.discountCodes || []).filter((c) => c.applicable).map((c) => c.code);
        // Union über alle Aliases — alle sollten dieselbe Code-Liste haben,
        // da wir bei jedem Alias die gleiche $codes Variable verwenden.
        aliasApplied.forEach((c) => { if (!appliedCodes.includes(c)) appliedCodes.push(c); });

        const lineNodes = (cart.lines && cart.lines.nodes) || [];
        if (!lineNodes.length) return;
        const line = lineNodes[0];

        const vid = gidToNumeric(line.merchandise && line.merchandise.id);
        if (!vid) return;

        const qty = Math.max(line.quantity || 1, 1);
        const subtotal = line.cost && line.cost.subtotalAmount ? toCents(line.cost.subtotalAmount.amount) : 0;

        // Bei Single-Variant-Cart entspricht cart.cost.totalAmount exakt dem
        // finalen Line-Preis (alle Discounts: line-level + order-level).
        // Keine Proportional-Verteilung nötig, da nur eine Line existiert.
        const cartTotalAmount = cart.cost && cart.cost.totalAmount
          ? toCents(cart.cost.totalAmount.amount)
          : subtotal;
        const finalLine = cartTotalAmount;
        const finalUnit = Math.round(finalLine / qty);
        const savings = Math.max(0, subtotal - finalLine);

        const lineCodes = (line.discountAllocations || [])
          .map((a) => a.code)
          .filter(Boolean);
        const codesForVariant = lineCodes.length ? lineCodes : aliasApplied.slice();

        previewMap[vid] = {
          variantId: vid,
          originalCents: subtotal,
          finalCents: finalUnit,
          finalLineCents: finalLine,
          savingsCents: savings,
          percentageOff: subtotal > 0 ? Math.round((savings * 100) / subtotal) : 0,
          discountCodes: codesForVariant
        };
      });

      return {
        previews: previewMap,
        appliedCodes: appliedCodes
      };
    } catch (e) {
      console.warn('[discount-preview] fetch failed', e);
      return null;
    }
  }

  window.__dpApi = {
    isEnabled: isEnabled,
    config: config,
    readCache: readCache,
    writeCache: writeCache,
    invalidateCache: invalidateCache,
    cacheKey: cacheKey,
    gidToNumeric: gidToNumeric,
    toCents: toCents,
    fetchCart: fetchCart,
    pushCodeToCart: pushCodeToCart,
    fireCartChange: fireCartChange,
    fetchStorefrontPreview: fetchStorefrontPreview
  };
})();
